/**
 * Cópia 1:1 das funções de cálculo que estão inline em
 * `apps/frontend/src/pages/VisaoComissoes.tsx` dentro de `exportarRelatorio`
 * (linhas ~494-563). Extraídas para que possam ser chamadas também fora
 * do contexto do botão de Excel — por exemplo, pelo painel "Por Equipe".
 *
 * IMPORTANTE: zero mudança lógica. Mesma matemática, mesmas variáveis,
 * mesma sequência. Se o Excel calcular X, este módulo calcula X.
 */
import { encontrarFaixa, encontrarFaixaSupervisor } from './regras';
import type { CommissionRule, MetaMensal } from './regras';
import { baseComissionavel } from './faturamento';
import { calcularComissaoRenovacaoDelta, type CommissionRuleRenovacaoDelta, type ItemComRenovacaoDelta } from './comissaoRenovacaoDelta';
import { renovacaoOk } from './renovacaoPorFibra';
import { fatorMigracaoJunta, metaBatidaPorMigracao } from './migracaoJunta';

export type CargoComissao = 'CONSULTOR' | 'SUPERVISOR' | 'COORDENADOR' | 'GERENTE';

/** Map role → cargo de comissão. Master/Diretor/Qualidade retornam null. */
export function mapCargoEspelho(role: string): CargoComissao | null {
  const u = role?.toUpperCase() || '';
  if (u === 'MASTER' || u === 'DIRETOR' || u === 'QUALIDADE') return null;
  if (u.includes('SUPERVISOR')) return 'SUPERVISOR';
  if (u.includes('COORDENADOR')) return 'COORDENADOR';
  if (u.includes('GERENTE')) return 'GERENTE';
  return 'CONSULTOR';
}

export const isBkoRoleEspelho = (role: string) =>
  role?.toLowerCase().startsWith('bko') || role?.toLowerCase() === 'bko';

export const isGestaoRoleEspelho = (role: string) => {
  const l = role?.toLowerCase() || '';
  // Master/Diretor não são "gestão de comissão" — são fantasmas e não recebem
  return ['supervisor', 'coordenador', 'gerente'].some(r => l.includes(r));
};

/** Regra do cargo, com fallback individual → genérico. */
export function getRulesForEspelho(
  allRulesGen: CommissionRule[],
  allRulesIndiv: CommissionRule[],
  cargo: string,
  userId: string,
  segmento: 'PF' | 'PJ' = 'PJ',
): CommissionRule[] {
  const indiv = allRulesIndiv.filter(r => r.usuario_id === userId);
  if (indiv.length > 0) return indiv;
  return regrasGeraisDoSegmento(allRulesGen, cargo, segmento);
}

/**
 * Regra geral (% por faixa) do cargo no segmento da pessoa (Douglas 21/10/2026:
 * a tabela de % separada por CNPJ e CPF, como as metas). Enquanto o CPF não
 * tiver a regra dele no mês, vale a do CNPJ: é o que valia até aqui.
 */
export function regrasGeraisDoSegmento(allRulesGen: CommissionRule[], cargo: string | undefined, segmento: 'PF' | 'PJ'): CommissionRule[] {
  const doCargo = allRulesGen.filter(r => !r.usuario_id && (!cargo || r.cargo === cargo));
  const doSegmento = doCargo.filter(r => (r.tipo_pessoa ?? 'PJ') === segmento);
  return doSegmento.length > 0 || segmento === 'PJ' ? doSegmento : doCargo.filter(r => (r.tipo_pessoa ?? 'PJ') === 'PJ');
}

/**
 * Resolvedor ÚNICO de meta do consultor. Usado por TODOS os motores de
 * cálculo (Excel, painel por equipe, gestão, BKO) para que nenhuma tela
 * divirja. Ordem de prioridade:
 *
 *   1. Individual (usuario_id) — exceção pontual por pessoa.
 *   2. Equipe (equipe_id)      — meta da equipe do consultor.
 *   3. Cargo (genérica)        — meta padrão de hoje.
 *
 * `metasEquipe`/`equipeId` são opcionais: sem eles, o comportamento é
 * idêntico ao histórico (individual → cargo).
 */
export function getMetaForEspelho(
  metasGen: MetaMensal[],
  metasIndiv: MetaMensal[],
  cargo: string,
  userId: string,
  metasEquipe: MetaMensal[] = [],
  equipeId?: string | null,
  segmento: 'PF' | 'PJ' = 'PJ',
): MetaMensal | null {
  const indiv = metasIndiv.find(m => m.usuario_id === userId);
  if (indiv) return indiv;
  if (equipeId) {
    const eq = metasEquipe.find(m => m.equipe_id === equipeId);
    if (eq) return eq;
  }
  // Meta geral por cargo é por segmento: consultor CPF nunca herda a meta geral CNPJ.
  return metasGen.find(m => m.cargo === cargo && (m.tipo_pessoa ?? 'PJ') === segmento) || null;
}

export interface FaturamentosCategorias {
  pn: number; ha: number; fibra: number; tt: number; ttpre: number; mp: number;
  /** (Douglas 28/07) Aglomerado T.I. (`produtos.fat_ti`). Opcional: chamadores antigos não passam. */
  ti?: number;
  /** (Douglas 28/07) Aglomerado Fixa Avançada (`produtos.fat_avancada`). */
  avancada?: number;
  total: number;
}
export interface QuantidadesCategorias {
  altas: number; fibras: number; tt: number; mp: number; migracao: number;
  sd?: number;
  tv?: number;
}

export function consultorBateuMetaDireta(
  meta: MetaMensal | null | undefined,
  faturamentos: FaturamentosCategorias,
  quantidades: QuantidadesCategorias,
): boolean {
  if (!meta) return false;
  // As três regras (Douglas 29/10/2026): quem entra pela migração bateu a meta.
  return avaliarMetaEspelho(meta, faturamentos, quantidades, 1).bateuMeta;
}

/**
 * A meta das três regras (Douglas 29/10/2026), a mesma no Excel
 * (`exportarRelatorio`) e na folha. `quantidadesMigracao` é o TT e a Renovação
 * da regra 3; no gestor vem sem a Diretoria. Sem ele, vale `qM`.
 */
export function avaliarMetaEspelho(
  meta: MetaMensal | null,
  fM: FaturamentosCategorias,
  qM: QuantidadesCategorias,
  n: number,
  quantidadesMigracao?: QuantidadesCategorias,
): { bateuMeta: boolean; porMigracao: boolean; fator: number } {
  if (!meta) return { bateuMeta: true, porMigracao: false, fator: 1 };
  const ttOk = !meta.usar_meta_qtd_tt || qM.tt >= meta.meta_qtd_tt * n;
  // (Douglas 26/10/2026) Fibra a mais valida a Renovação; no gestor, o número × N (Douglas 29/10/2026).
  const renovacaoBatida = renovacaoOk({ usar: meta.usar_meta_qtd_mp, renovacao: qM.mp, metaRenovacao: meta.meta_qtd_mp * n, fibras: qM.fibras, metaFibras: meta.meta_fibras * n, regra: meta, n });
  const outros =
    (!meta.usar_meta_altas || qM.altas >= meta.meta_altas * n) &&
    (!meta.usar_meta_fibras || qM.fibras >= meta.meta_fibras * n) &&
    (!meta.usar_meta_fat_vn || (fM.pn + fM.ha) >= meta.meta_fat_vn * n) &&
    (!meta.usar_meta_fat_total || fM.total >= meta.meta_fat_total * n) &&
    (!(meta.usar_meta_qtd_migracao ?? false) || qM.migracao >= (meta.meta_qtd_migracao ?? 0) * n) &&
    // (Douglas 28/07) Metas de FATURAMENTO de T.I. / Fixa Avançada.
    (!(meta.usar_meta_fat_ti ?? false) || Number(fM.ti ?? 0) >= Number(meta.meta_fat_ti ?? 0) * n) &&
    (!(meta.usar_meta_fat_avancada ?? false) || Number(fM.avancada ?? 0) >= Number(meta.meta_fat_avancada ?? 0) * n) &&
    // (Douglas 29/08) SD por quantidade de licenças.
    (!((meta as { usar_meta_sd?: boolean }).usar_meta_sd ?? false) || Number(qM.sd ?? 0) >= Number((meta as { meta_sd?: number }).meta_sd ?? 0) * n) &&
    // (Douglas 21/10/2026) TV CPF por quantidade — meta só do CPF.
    (!(meta.usar_meta_tv ?? false) || Number(qM.tv ?? 0) >= Number(meta.meta_tv ?? 0) * n);
  const bateuSeparada = outros && ttOk && renovacaoBatida;
  const qJ = quantidadesMigracao ?? qM;
  const porMigracao = metaBatidaPorMigracao({
    regra: meta, bateuSeparada, bateuSemTtRenovacao: outros,
    usarTt: meta.usar_meta_qtd_tt, usarRenovacao: meta.usar_meta_qtd_mp,
    tt: qJ.tt, renovacao: qJ.mp, metaTt: meta.meta_qtd_tt * n, metaRenovacao: meta.meta_qtd_mp * n,
  });
  return { bateuMeta: bateuSeparada || porMigracao, porMigracao, fator: porMigracao ? fatorMigracaoJunta(meta) : 1 };
}

/**
 * Calcula comissão e flag de meta. Cópia idêntica do Excel
 * (`exportarRelatorio` linhas ~520-563).
 */
export function calcComissaoEspelho(
  rules: CommissionRule[],
  faturamentos: FaturamentosCategorias,
  quantidades: QuantidadesCategorias,
  meta: MetaMensal | null,
  nVendedores: number,
  faturamentosMeta?: FaturamentosCategorias,
  quantidadesMeta?: QuantidadesCategorias,
  matrizRenovacao: CommissionRuleRenovacaoDelta[] = [],
  itensRenovacao: ItemComRenovacaoDelta[] = [],
  quantidadesMigracao?: QuantidadesCategorias,
) {
  if (rules.length === 0) {
    return { comissao: 0, bateuMeta: false, porMigracao: false, faixa: 0, pctPn: 0, pctHa: 0, pctFibra: 0, pctTt: 0, pctTtPre: 0, pctMp: 0, pctTi: 0, pctAvancada: 0 };
  }

  const qM = quantidadesMeta ?? quantidades;
  const fM = faturamentosMeta ?? faturamentos;
  const multiplicadorN = meta?.multiplicador_n && nVendedores > 0;
  const n = multiplicadorN ? nVendedores : 1;

  // Find faixa
  let faixaBase: number;
  if (multiplicadorN) {
    const r = encontrarFaixaSupervisor(faturamentos.total, nVendedores);
    faixaBase = r.faixaBase;
  } else {
    faixaBase = encontrarFaixa(faturamentos.total);
  }
  const regra = rules.find(r => r.faixa_faturamento === faixaBase);
  if (!regra) {
    return { comissao: 0, bateuMeta: false, porMigracao: false, faixa: faixaBase, pctPn: 0, pctHa: 0, pctFibra: 0, pctTt: 0, pctTtPre: 0, pctMp: 0, pctTi: 0, pctAvancada: 0 };
  }

  const { bateuMeta, porMigracao, fator } = avaliarMetaEspelho(meta, fM, qM, n, quantidadesMigracao);

  const pctPn = regra.pn_pct, pctHa = regra.ha_pct, pctFibra = regra.fibra_pct, pctTt = regra.tt_pct, pctTtPre = regra.tt_pre_pct ?? regra.tt_pct, pctMp = regra.mp_pct;
  // T.I. e Avançada pagam como as demais categorias. `?? 0` = regra antiga sem a
  // coluna não paga (e nunca vira NaN).
  const pctTi = Number(regra.ti_pct ?? 0), pctAvancada = Number(regra.avancada_pct ?? 0);
  // Fora sul paga metade do % (Douglas 26/09/2026); faixa e meta seguem inteiras.
  const base = baseComissionavel(faturamentos);
  const comissaoRenovacao = calcularComissaoRenovacaoDelta(base.mp, regra.id, pctMp, matrizRenovacao, itensRenovacao);
  const comissaoCheia = (base.pn * pctPn + base.ha * pctHa + base.fibra * pctFibra + base.tt * pctTt + base.ttpre * pctTtPre + base.ti * pctTi + base.avancada * pctAvancada) / 100 + comissaoRenovacao.comissaoTotal;
  // Regra 3: meta batida por migração paga a parte configurada (50%).
  const comissao = comissaoCheia * fator;

  return { comissao, bateuMeta, porMigracao, faixa: faixaBase, pctPn, pctHa, pctFibra, pctTt, pctTtPre, pctMp, pctTi, pctAvancada };
}

/** Tipo do resumo por funcionário — espelha a estrutura `LinhaResumo` do Excel. */
export interface LinhaResumoEspelho {
  id: string;
  nome: string;
  cargo: string;             // 'CONSULTOR' | 'SUPERVISOR' | ... | 'BKO' | 'CONSULTOR DIRETORIA' | 'SUPERVISOR QUALIDADE' | 'ANALISTA QUALIDADE'
  equipe: string;            // nome da equipe (ou '—' / 'GERÊNCIA' virtual)
  fatPn: number; fatHa: number; fatFibra: number; fatTt: number; fatTtPre: number; fatMp: number; fatTotal: number;
  pctPn: number; pctHa: number; pctFibra: number; pctTt: number; pctTtPre: number; pctMp: number;
  /** T.I. e Fixa Avançada entram na comissão (comissao-consultor.md); sem elas as colunas não fecham com o total. */
  fatTi?: number; fatAvancada?: number; pctTi?: number; pctAvancada?: number;
  bateuMeta: 'SIM ✓' | 'NÃO' | '—' | 'DIRETORIA';
  /** (Douglas 29/10/2026) Bateu pela regra 3 (TT + Renovação juntos): a comissão já vem com os 50%. */
  metaPorMigracao?: boolean;
  faixa: number;
  comissao: number;          // comissão da regra (zerada se não bateu meta)
  rateioRetencao: number;    // só Qualidade
  bonusKpi: number;          // só Supervisora Backoffice
  /**
   * BALANÇA DE CONVERGÊNCIA (Douglas 29/07). O card "Por Pessoa" sempre aplicou
   * perda/ganho ao valor final; a aba "Por Equipe" e o Excel não — por isso os
   * dois números não batiam. Ficam explícitos aqui pra dar pra conferir a
   * diferença, não só o total.
   */
  perdaConvergencia: number;   // desconto por convergência NÃO feita
  ganhoConvergencia: number;   // acréscimo por convergência feita (só se bateu meta)
  saldoConvergencia: number;   // ganho − perda
  /** (Douglas 31/07) Deflator por excesso de cancelamento — já dentro do totalReceber. */
  pctCancelado: number;        // % de cancelamento medido na base configurada
  descontoCancelamento: number;// R$ que saiu da produtividade
  totalReceber: number;      // soma final (já com a balança e o deflator)
  /** Categorias do funcionário — para o painel destacar Diretoria/Qualidade/Gerência */
  isBko: boolean;
  isDiretoria: boolean;
  /** Diretoria: saldo de estorno que veio do mês anterior e o que passa para o próximo. */
  saldoEstornoAnterior?: number;
  /** Comissão antes das balanças, antes do eSIM, e o desconto de eSIM/chip não validado. */
  comissaoBruta?: number;
  comissaoAntesEsim?: number;
  descontoEsim?: number;
  saldoEstornoTransportado?: number;
  isQualidade: boolean;
  isGestao: boolean;
  emExperiencia: boolean;    // chip 🎓 no painel
  /** Gestor com equipe nos dois segmentos (de 2026-11): o lado do outro segmento,
   *  já somado no totalReceber. Os campos de faturamento acima são do segmento dele. */
  outrosSegmentos?: Array<{ segmento: 'PF' | 'PJ'; comissao: number; fatTotal: number; bateuMeta: boolean; faixa: number }>;
  /** Nota do mês do cargo operacional (0..100) */
  notaBko?: number;
  /** % de produtividade liberado pela nota (0..100) */
  pctMetaNota?: number;
}
